<script setup lang="ts">
  import { type RuleExpression, useField } from 'vee-validate'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { defineProps, defineEmits, type PropType, computed, ref } from 'vue'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [String, Number, null] as PropType<string | number | null>,
      required: false
    },
    type: {
      type: String,
      required: false,
      default: 'text'
    },
    step: {
      type: String,
      required: false
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    error: {
      type: String,
      required: false
    },
    class: {
      type: String,
      required: false,
      default: ''
    },
    suffix: {
      type: String,
      required: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    asterisk: {
      type: Boolean,
      required: false,
      default: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'input', 'blur', 'focus', 'validation-state'])

  const {
    value,
    errorMessage: internalErrorMessage,
    handleBlur,
    handleChange,
    meta,
    errors
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const touched = ref(false)

  const errorMessage = computed(() => {
    if (props.error) {
      return props.error
    }
    return touched.value ? internalErrorMessage.value : ''
  })

  const emitInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    emit('input', target.value)
    emit('update:modelValue', target.value)
    handleChange(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
  }

  const emitChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    emit('update:modelValue', target.value)
    handleChange(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
  }

  const handleInputBlur = (event: Event) => {
    touched.value = true
    handleBlur(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
    emit('blur', event)
  }

  const handleInputFocus = (event: FocusEvent) => {
    touched.value = true
    emit('focus', event)
  }
</script>

<template>
  <label
    v-if="label"
    class="mb-2 block text-sm font-medium"
  >
    {{ label }}
    <span
      v-if="asterisk"
      class="text-base font-normal text-red-500"
    >
      *
    </span>
  </label>
  <div :class="errorMessage || suffix ? 'relative' : ''">
    <input
      v-model="value"
      class="block w-full rounded-lg px-3 py-2 text-sm text-gray-800 placeholder:text-gray-500 focus:z-10"
      :name="props.name"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :class="[
        errorMessage
          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
          : 'border-gray-200 focus:border-purple-600 focus:ring-purple-600',
        disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-text bg-white',
        suffix ? 'pr-10' : ''
      ]"
      @input="emitInput"
      @change="emitChange"
      @blur="handleInputBlur"
      @focus="handleInputFocus"
    />
    <div
      v-if="suffix"
      class="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3 text-base font-light
        text-gray-500"
    >
      {{ suffix }}
    </div>
    <div
      v-if="errorMessage"
      class="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3"
      :class="{ 'opacity-50': disabled }"
    >
      <ExclamationCircleIcon class="size-5 flex-shrink-0 text-red-500" />
    </div>
  </div>
  <p
    v-if="errorMessage"
    class="mt-1 text-xs text-red-600"
    :class="{ 'opacity-50': disabled }"
  >
    {{ errorMessage }}
  </p>
</template>
